import type { AxiosResponse } from 'axios';
import apiClient from './api';

/**
 * Parse the download filename from a `Content-Disposition` header, honouring the RFC 5987
 * `filename*=UTF-8''…` form first, then a quoted `filename="…"`, then a bare `filename=…`.
 * Returns null when no filename token is present.
 */
function parseContentDispositionFilename(contentDisposition: string | undefined): string | null {
  if (!contentDisposition) return null;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
  const quotedMatch = contentDisposition.match(/filename="(.+?)"/);
  const plainMatch = contentDisposition.match(/filename=([^;\s]+)/);
  const match = utf8Match || quotedMatch || plainMatch;
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Trigger a browser download for a blob response, using (in precedence order) an explicit
 * filename, the server-provided `Content-Disposition` filename, a caller-supplied fallback
 * (`fallbackFilename` — for non-`.xlsx` endpoints such as the Smart Sampling `.zip` exports),
 * then the recommendation default (`DRIS_RECOMMENDATION_Batch_{batchId}.xlsx`).
 */
export function triggerBlobDownload(
  response: AxiosResponse,
  batchId: number,
  filename?: string,
  fallbackFilename?: string,
): void {
  const serverFilename = parseContentDispositionFilename(response.headers?.['content-disposition']);
  const downloadFilename =
    filename || serverFilename || fallbackFilename || `DRIS_RECOMMENDATION_Batch_${batchId}.xlsx`;

  const objectUrl = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = downloadFilename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(objectUrl);
}

/**
 * Trigger a browser download by navigating to a ready-made URL (e.g. a short-lived presigned S3 URL
 * for a durable async export). The object is served with `Content-Disposition: attachment` + a
 * sanitized filename, so the browser downloads it rather than navigating away; the `download`
 * attribute is intentionally omitted (it is ignored for cross-origin hrefs anyway). No blob is
 * fetched through the app — the big file never re-proxies through the API/nginx.
 */
export function triggerUrlDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Fetch an .xlsx blob from the given URL and trigger a browser download (mirrors the
 * batchCombinedService server-built ClosedXML pattern — server-built blob, server-provided
 * filename). Shared by both the Agent and Partner snapshot clients.
 */
export async function downloadRecommendationsExcel(url: string, batchId: number, filename?: string): Promise<void> {
  const response = await apiClient.get(url, { responseType: 'blob', timeout: 300000 });
  triggerBlobDownload(response, batchId, filename);
}
